import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { SupportTicketSystemService } from '../support-ticket-system.service';
import {
  EmailPreviewData,
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
} from '../support-ticket-system.models';

interface AdminTicket extends SupportTicket {
  userType: string;
  ticketSource?: string;
  contactEmail?: string;
  ticketId?: number | string;
}

@Component({
  selector: 'app-admin-support-ticket-system',
  templateUrl: './admin-support-ticket-system.component.html',
  styleUrls: ['./admin-support-ticket-system.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AdminSupportTicketSystemComponent implements OnInit {
  adminName = 'Support Admin';
  adminRole = 'Admin Dashboard';
  adminEmail = 'mps@varuna-sentinel.com';
  systemFromEmail = 'system@marineportal.com';
  searchQuery = '';
  filterStatus = 'all';
  limit = 10;
  offset = 0;
  total = 0;
  stats = { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 };
  loading = false;
  currentPage = 1;

  tickets: AdminTicket[] = [];
  selectedTicket: AdminTicket | null = null;
  emailPreview: EmailPreviewData | null = null;

  showDetailsModal = false;
  showEmailModal = false;
  showStatusModal = false;
  statusUpdateOptions: SupportTicketStatus[] = [];
  pendingStatusTicket: AdminTicket | null = null;

  constructor(
    private supportTicketService: SupportTicketSystemService,
    private fullLoader: FullLoaderService
  ) {}

  ngOnInit(): void {
    this.loadTickets();
  }

  get userInitials(): string {
    return this.adminName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  setFilter(filter: string): void {
    this.filterStatus = filter;
    this.offset = 0;
    this.currentPage = 1;
    this.loadTickets();
  }

  openTicketDetails(ticket: AdminTicket): void {
    this.selectedTicket = ticket;
    this.showDetailsModal = true;
  }

  openStatusModal(ticket: AdminTicket): void {
    this.pendingStatusTicket = ticket;
    this.statusUpdateOptions = this.getStatusUpdateOptions(ticket.status);
    this.showStatusModal = true;
  }

  async submitStatusUpdate(newStatus: string): Promise<void> {
    const nextStatus = this.validateStatus(newStatus);
    if (!this.pendingStatusTicket || !nextStatus) {
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      return;
    }

    const ticketId = this.pendingStatusTicket.ticketId;
    if (!ticketId || !this.pendingStatusTicket.ticketSource) {
      console.warn('Missing ticketId or ticketSource for status update.');
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      return;
    }

    this.fullLoader.showLoader('Updating ticket status...');
    try {
      await this.supportTicketService.updateTicketStatusByAdmin({
        ticketId: Number(ticketId),
        ticketSource: this.pendingStatusTicket.ticketSource,
        status: nextStatus,
      });
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      await this.loadTickets();
    } catch (err) {
      console.error('Failed to update ticket status', err);
    } finally {
      this.fullLoader.hideLoader();
    }
  }

  openEmailPreview(ticket: AdminTicket): void {
    this.emailPreview = this.buildEmailPreviewFromTicket(ticket);
    this.showEmailModal = true;
  }

  closeModals(): void {
    this.showDetailsModal = false;
    this.showEmailModal = false;
    this.showStatusModal = false;
    this.pendingStatusTicket = null;
  }

  printEmailPreview(): void {
    const preview = document.getElementById('adminEmailPreview');
    if (!preview) {
      console.warn('Email preview not found for printing.');
      return;
    }

    const ticketRef = this.selectedTicket?.ticketRefNumber || this.selectedTicket?.id || 'Ticket';
    const roleLabel = (this.selectedTicket?.ticketSource || 'Admin').toString();
    const title = `${roleLabel}_Support_Ticket_System_${ticketRef}.pdf`;
    const printMarkup = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1f2d3d; }
            .email-preview { max-width: 760px; margin: 0 auto; }
            .email-header { margin-bottom: 16px; }
            .email-to { margin-bottom: 6px; font-size: 14px; }
            .email-body h4 { margin: 16px 0 8px; }
            .email-body p, .email-body li { font-size: 14px; line-height: 1.5; }
            .email-body ul, .email-body ol { padding-left: 20px; }
          </style>
        </head>
        <body>
          ${preview.innerHTML}
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentWindow?.document;
    if (!frameDoc || !iframe.contentWindow) {
      console.warn('Unable to access print frame.');
      document.body.removeChild(iframe);
      return;
    }

    frameDoc.open();
    frameDoc.write(printMarkup);
    frameDoc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 500);
  }

  getStatusClass(status: SupportTicketStatus): string {
    switch (status) {
      case 'Open':
        return 'status-open';
      case 'In Progress':
        return 'status-in-progress';
      case 'Resolved':
        return 'status-resolved';
      case 'Closed':
        return 'status-closed';
      default:
        return '';
    }
  }

  getPriorityClass(priority: SupportTicketPriority): string {
    switch (priority) {
      case 'High':
        return 'priority-high';
      case 'Medium':
        return 'priority-medium';
      case 'Low':
        return 'priority-low';
      case 'Urgent':
        return 'priority-urgent';
      default:
        return '';
    }
  }

  downloadAttachment(name: string): void {
    const attachment =
      this.selectedTicket?.attachments.find((a) => a.name === name) ||
      this.selectedTicket?.attachments[0];
    if (!attachment?.link) {
      console.warn(`No link found for attachment: ${attachment?.name || name}`);
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = attachment.link;
    anchor.download = attachment.name || 'attachment';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  onSearchChange(): void {
    this.offset = 0;
    this.currentPage = 1;
    this.loadTickets();
  }

  prevPage(): void {
    if (this.currentPage <= 1) return;
    this.currentPage -= 1;
    this.offset = (this.currentPage - 1) * this.limit;
    this.loadTickets();
  }

  nextPage(): void {
    if (this.currentPage >= this.totalPagesForStatus) return;
    this.currentPage += 1;
    this.offset = (this.currentPage - 1) * this.limit;
    this.loadTickets();
  }

  onPageSizeChange(): void {
    this.offset = 0;
    this.currentPage = 1;
    this.loadTickets();
  }

  get totalPages(): number {
    return this.total > 0 ? Math.ceil(this.total / this.limit) : 1;
  }

  get totalPagesForStatus(): number {
    const totalForStatus = this.currentStatusTotal;
    return totalForStatus > 0 ? Math.ceil(totalForStatus / this.limit) : 1;
  }

  get currentStatusTotal(): number {
    switch (this.filterStatus) {
      case 'open':
        return this.stats.open;
      case 'inProgress':
        return this.stats.inProgress;
      case 'resolved':
        return this.stats.resolved;
      case 'closed':
        return this.stats.closed;
      default:
        return this.stats.total;
    }
  }

  private async loadTickets(): Promise<void> {
    this.loading = true;
    this.fullLoader.showLoader('Loading support tickets...');
    try {
      const status = this.mapFilterToStatus(this.filterStatus);
      const response = await this.supportTicketService.listAdminTickets({
        limit: this.limit,
        offset: this.offset,
        status: status || undefined,
        searchTerm: this.searchQuery || undefined,
      });

      this.tickets = (response?.rows || []).map((row: any) =>
        this.mapApiTicket(row)
      );
      this.total = response?.totalElements || this.tickets.length;
      this.stats.total = response?.totalElements || this.tickets.length;
      this.stats.open = response?.openTotalCount || 0;
      this.stats.inProgress = response?.inProgressTotalCount || 0;
      this.stats.resolved = response?.resolvedTotalCount || 0;
      this.stats.closed = response?.closedTotalCount || 0;

      if (this.tickets.length) {
        this.selectedTicket = this.tickets[0];
        this.emailPreview = this.buildEmailPreviewFromTicket(this.selectedTicket);
      } else {
        this.selectedTicket = null;
        this.emailPreview = null;
      }
    } catch (err) {
      console.error('Failed to load admin support tickets', err);
    } finally {
      this.loading = false;
      this.fullLoader.hideLoader();
    }
  }

  private mapApiTicket(row: any): AdminTicket {
    const createdDate = row?.createdAt
      ? new Date(row.createdAt).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    const attachments = this.parseAttachments(row?.attachments);
    return {
      id: row?.ticketRefNumber || row?.id || '',
      ticketRefNumber: row?.ticketRefNumber || row?.id,
      ticketId: row?.id,
      subject: row?.subject || 'Support Ticket',
      category: row?.category || 'General',
      status: this.formatStatus(row?.status || 'Open'),
      priority: this.formatPriority(row?.priority || 'Medium'),
      created: createdDate,
      submitter: `${row?.accountName || row?.contactEmail || 'User'} (${row?.contactEmail || ''})`,
      description: row?.description || '',
      attachments,
      userType: row?.ticketSource === 'Supplier' ? 'Supplier' : 'Buyer',
      ticketSource: row?.ticketSource,
      contactEmail: row?.contactEmail,
      buyerName: row?.accountName,
      emails: row?.contactEmail ? [row.contactEmail] : [],
    };
  }

  private getStatusUpdateOptions(status: SupportTicketStatus): SupportTicketStatus[] {
    if (status === 'Open') return ['In Progress', 'Resolved'];
    if (status === 'In Progress') return ['Resolved'];
    return [];
  }

  private validateStatus(status: string): SupportTicketStatus | null {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'open') return 'Open';
    if (normalized === 'in progress' || normalized === 'in_progress') return 'In Progress';
    if (normalized === 'resolved') return 'Resolved';
    if (normalized === 'closed') return 'Closed';
    return null;
  }

  protected buildEmailPreviewFromTicket(ticket: AdminTicket): EmailPreviewData {
    return {
      fromEmail: ticket.submitter,
      subject: `[SUPPORT TICKET ${ticket.id}] Ticket Details for Admin Review`,
      ticketId: ticket.id,
      submitter: ticket.submitter,
      userEmail: ticket.contactEmail || ticket.submitter,
      category: ticket.category,
      priority: ticket.priority,
      date: this.getFormattedDate(ticket.created),
      description: ticket.description,
    };
  }

  protected getFormattedDate(dateValue: string): string {
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private parseAttachments(raw: any): { name: string; size: string; icon: string; link?: string }[] {
    if (!raw) return [];

    let parsed: any[] = [];
    try {
      if (typeof raw === 'string') {
        parsed = JSON.parse(raw);
      } else if (Array.isArray(raw)) {
        parsed = raw;
      }
    } catch {
      parsed = [];
    }

    return parsed
      .filter((doc) => !!doc)
      .map((doc) => ({
        name: doc.documentName || doc.name || 'Attachment',
        size: '',
        icon: doc.icon || 'fas fa-file',
        link: doc.documentFileS3Path || doc.s3path || doc.url || doc.link || '',
      }));
  }

  private formatPriority(priority: string): SupportTicketPriority {
    const normalized = (priority || '').toLowerCase();
    switch (normalized) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'urgent':
        return 'Urgent';
      default:
        return 'Medium';
    }
  }

  private formatStatus(status: string): SupportTicketStatus {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'open':
        return 'Open';
      case 'in progress':
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return 'Open';
    }
  }

  private mapFilterToStatus(filter: string): string | undefined {
    switch (filter) {
      case 'open':
        return 'Open';
      case 'inProgress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return undefined;
    }
  }
}
